import localeConfig from "../../../config/locales";

/**
 * US launch (WHO-5766). One switch for everything the site says about the US
 * before launch day, so production is unchanged until it is flipped.
 *
 * When on:
 *   - `/en-us` is a routed locale: English content, USD prices from the path
 *     (`config/locales.js`, `LOCALE_CURRENCIES`);
 *   - every hreflang cluster that has an `en` alternate also lists
 *     `hreflang="en-US"`, pointing at the same page under `/en-us`;
 *   - the Organization `areaServed` in `_document` lists the US.
 *
 * Not covered: Offer `shippingDetails` (US rates and transit times are not
 * known yet, and Google needs a rate); USD in structured data and feeds:
 * those stay in euros (decision D3); and a US copy variant of the listing
 * title templates (US spelling, "Pre-owned", a USD price-from). `/en-us` now
 * tells a US request apart; the templates don't use it yet.
 *
 * `next.config.js` inlines the value into the server and client bundles: the
 * hreflang tags are rendered by client components too, and a server-only
 * value would drop the tag again on hydration. Flipping it therefore needs a
 * redeploy. Unset, empty or anything other than `1` / `true` = off.
 */
export const US_LAUNCH_ENABLED_ENV = "US_LAUNCH_ENABLED";

export const US_HREFLANG = "en-US";

/** The URL prefix of the US locale (`config/locales.js`). */
export const US_PATH_PREFIX = "/en-us";
export const US_COUNTRY_CODE = "US";

/** The locale whose page the en-US alternate is the US version of. */
export const US_HREFLANG_SOURCE_LOCALE = "en";

/** One implementation, in `config/locales.js`, which the build-time config also reads. */
export const isUsLaunchEnabled: (value?: string) => boolean =
  localeConfig.isUsLaunchEnabled;

/**
 * The `/en-us` version of an English URL: `<host>/furniture`
 * -> `<host>/en-us/furniture`. Relative paths work too.
 */
export const toUsHref = (enHref: string): string => {
  const absolute = /^https?:\/\//.test(enHref);
  // A parse base for relative paths only; their origin is dropped again below.
  const url = new URL(enHref, "https://parse.invalid");
  url.pathname = `${US_PATH_PREFIX}${url.pathname === "/" ? "" : url.pathname}`;

  return absolute ? url.href : `${url.pathname}${url.search}${url.hash}`;
};

const isUsAlternate = (locale: string) =>
  locale.toLowerCase() === US_HREFLANG.toLowerCase();

/**
 * The cluster with exactly one en-US alternate when the flag is on: the `en`
 * page under `/en-us`. Any en-US entry a caller built itself is dropped first,
 * so a cluster read off the routed locales (which include `en-us` while the
 * flag is on) never lists it twice. Flag off, or no `en` entry: the input
 * unchanged.
 */
export const withUsHreflang = <T extends { locale: string; href: string }>(
  alternates: T[],
  enabled: boolean = isUsLaunchEnabled()
): Array<T | { locale: string; href: string }> => {
  if (!enabled) {
    return alternates;
  }

  const en = alternates.find(
    alternate => alternate.locale === US_HREFLANG_SOURCE_LOCALE
  );

  if (!en) {
    return alternates;
  }

  return [
    ...alternates.filter(alternate => !isUsAlternate(alternate.locale)),
    { locale: US_HREFLANG, href: toUsHref(en.href) },
  ];
};

/** The countries for Organization `areaServed`, with the US when the flag is on. */
export const withUsCountry = <T extends string>(
  countries: readonly T[],
  enabled: boolean = isUsLaunchEnabled()
): string[] => (enabled ? [...countries, US_COUNTRY_CODE] : [...countries]);
